import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, RefreshCcw, Save, Sparkles, XCircle } from 'lucide-react';

/** The tenant's LLM, which the Copilot uses like every other feature. */
interface TenantLlm {
  source: 'TENANT_DEFAULT' | 'NONE';
  provider: string | null;
  label: string;
  model: string | null;
  fit: 'RECOMMENDED' | 'NOT_RECOMMENDED' | 'UNMEASURED';
  maxTokensCap: number | null;
  capBelowCopilot: boolean;
  usable: boolean;
}

/** GET/PUT /api/admin/llm/copilot-policy/tenant/{id} (F-BR-003). */
interface CopilotPolicyView {
  abfLicensed: boolean;
  copilotLicensed: boolean;
  loansLicensed?: boolean;
  visibleToUsers: boolean;
  enabled: boolean;
  planner: boolean;
  explain: boolean;
  review: boolean;
  funding: boolean;
  drafts: boolean;
  insights: boolean;
  toolCallBudget: number;
  memoryTurns: number;
  updatedBy: string | null;
  updatedAt: string | null;
  llm: TenantLlm;
}

type Settings = Pick<CopilotPolicyView, 'enabled' | 'planner' | 'explain' | 'review'
  | 'funding' | 'drafts' | 'insights' | 'toolCallBudget' | 'memoryTurns'>;

const settingsOf = (view: CopilotPolicyView): Settings => ({
  enabled: view.enabled, planner: view.planner,
  explain: view.explain, review: view.review, funding: view.funding, drafts: view.drafts,
  insights: view.insights, toolCallBudget: view.toolCallBudget, memoryTurns: view.memoryTurns,
});

const Toggle: React.FC<{ title: string; detail: string; on: boolean; disabled?: boolean; onChange: (on: boolean) => void }> =
  ({ title, detail, on, disabled, onChange }) => (
    <div className="flex items-center justify-between p-4 bg-slate-950/50 rounded-xl border border-white/5">
      <div className="pr-4">
        <div className="text-sm font-bold text-white">{title}</div>
        <div className="text-xs text-slate-500">{detail}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={title}
        disabled={disabled}
        onClick={() => onChange(!on)}
        className={`w-12 h-6 shrink-0 rounded-full transition-all relative disabled:opacity-40 ${on ? 'bg-emerald-600' : 'bg-slate-700'}`}
      >
        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${on ? 'left-7' : 'left-1'}`} />
      </button>
    </div>
  );

const Licence: React.FC<{ label: string; on: boolean }> = ({ label, on }) => (
  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
    on ? 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10' : 'text-amber-300 border-amber-500/30 bg-amber-500/10'}`}>
    {on ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
    {label}: {on ? 'licensed' : 'not licensed'}
  </span>
);

/**
 * Tenant Admin → LLM Configuration → ABF Copilot. The platform admin licenses the Copilot in License
 * Master; the tenant admin switches it on here and chooses which LLM answers.
 */
const AbfCopilotSettingsCard: React.FC<{ tenantId: string; refreshKey?: string }> = ({ tenantId, refreshKey }) => {
  const [view, setView] = useState<CopilotPolicyView | null>(null);
  const [draft, setDraft] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.get<CopilotPolicyView>(`/api/admin/llm/copilot-policy/tenant/${tenantId}`);
      setView(data);
      setDraft(settingsOf(data));
    } catch (err) {
      setView(null);
      setError(axios.isAxiosError(err) && err.response?.data?.message
        ? String(err.response.data.message) : 'The Copilot settings could not be loaded.');
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const { data } = await axios.put<CopilotPolicyView>(`/api/admin/llm/copilot-policy/tenant/${tenantId}`, draft);
      setView(data);
      setDraft(settingsOf(data));
      setSaved(true);
    } catch (err) {
      setError(axios.isAxiosError(err) && err.response?.data?.message
        ? String(err.response.data.message) : 'The Copilot settings were not saved.');
    } finally {
      setSaving(false);
    }
  };

  if (!view || !draft) {
    return (
      <div className="rounded-2xl border border-white/10 bg-slate-800/50 p-6 text-sm text-slate-400">
        {error ?? 'Loading Ask Axiom settings…'}
      </div>
    );
  }

  const set = (patch: Partial<Settings>) => { setSaved(false); setDraft({ ...draft, ...patch }); };
  const llm = view.llm;
  const licensed = (view.abfLicensed || !!view.loansLicensed) && view.copilotLicensed;
  const where = [view.abfLicensed ? 'ABF facilities and forward-flow programs' : null,
    view.loansLicensed ? 'private credit and leveraged loans' : null].filter(Boolean).join(' and ');
  const changed = JSON.stringify(draft) !== JSON.stringify(settingsOf(view));
  const whyHidden = !view.copilotLicensed ? 'Ask Axiom is not licensed. Your platform administrator grants it in License Master.'
    : !view.abfLicensed && !view.loansLicensed ? 'Neither Asset-Based Finance nor Leveraged Loans is licensed for this tenant.'
    : !view.enabled ? 'It is switched off below.' : null;

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-800/50 p-6 space-y-5">
      <div className="flex justify-between items-start gap-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-sky-300" /> Ask Axiom
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Grounded answers with sources on ABF facilities, forward-flow programs, private credit and leveraged loans, as
            licensed. Your platform administrator licenses it; you switch it on here.
            It answers with this tenant&apos;s LLM, like every other AI feature.
          </p>
        </div>
        <button type="button" onClick={() => void load()} aria-label="Reload Copilot settings"
          className="p-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-slate-400 hover:text-white border border-white/5">
          <RefreshCcw className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Licence label="Ask Axiom" on={view.copilotLicensed} />
        <Licence label="Asset-Based Finance" on={view.abfLicensed} />
        <Licence label="Leveraged Loans" on={!!view.loansLicensed} />
        <span className={`text-sm ${view.visibleToUsers ? 'text-emerald-300' : 'text-slate-400'}`}>
          {view.visibleToUsers ? `Users see Ask Axiom on ${where}.` : `Users do not see Ask Axiom. ${whyHidden ?? ''}`}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Toggle title="Ask Axiom on" detail="Show Ask Axiom to this tenant's users wherever it is licensed."
          on={draft.enabled} onChange={enabled => set({ enabled })} />
        <Toggle title="Grounded answers" detail="The LLM plans its reads and writes the answer. Off: fixed-text answers on ABF facilities only, no LLM call; program and loan pages need it on."
          on={draft.planner} onChange={planner => set({ planner })} />
      </div>

      <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5 text-sm">
        <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">LLM that answers</div>
        {llm.source === 'NONE' ? (
          <p className="text-amber-300">
            No LLM is selected for this tenant. Choose one in LLM Configuration above (Make default): until then every
            AI feature, the Copilot included, returns an error instead of falling back to another provider.
          </p>
        ) : (
          <div className="text-white">
            {llm.label}{llm.model ? ` · ${llm.model}` : ' · provider default model'}
            <span className="text-slate-500"> (this tenant&apos;s default in LLM Configuration above)</span>
          </div>
        )}
        {llm.source !== 'NONE' && !llm.usable && <p className="text-amber-300 mt-1">This LLM cannot answer on this server. Fix it in LLM Configuration above.</p>}
        {llm.fit === 'RECOMMENDED' && <p className="text-emerald-300 mt-1">Measured on Ask Axiom&apos;s question set and recommended.</p>}
        {llm.fit === 'NOT_RECOMMENDED' && <p className="text-amber-300 mt-1">Measured on Ask Axiom&apos;s question set and not recommended: expect weaker, less reliable answers.</p>}
        {llm.source !== 'NONE' && llm.fit === 'UNMEASURED' && <p className="text-slate-400 mt-1">Not yet measured on Ask Axiom&apos;s question set.</p>}
        {llm.capBelowCopilot && <p className="text-amber-300 mt-1">Max tokens per call ({llm.maxTokensCap}) is below the 3,000 Ask Axiom asks for; long answers will be cut.</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Toggle title="Explain figures" detail="Break down headline figures: availability and borrowing base on ABF facilities; funded, commitment, rate, readiness and LME score on loans."
          on={draft.explain} onChange={explain => set({ explain })} />
        <Toggle title="Prepare reviews" detail="ABF facilities only. Period comparisons and facility reviews, saved with their evidence."
          on={draft.review} onChange={review => set({ review })} />
        <Toggle title="Funding drafts" detail="ABF facilities only. Draft a draw request; it still goes through four-eyes approval."
          on={draft.funding} onChange={funding => set({ funding })} />
        <Toggle title="Activity-note drafts" detail="ABF facilities only. Draft notes for the facility's Activity log after the user confirms."
          on={draft.drafts} onChange={drafts => set({ drafts })} />
        <Toggle title="Proposed insights" detail="ABF facilities only. Suggest work for the queue; a person approves or skips each one."
          on={draft.insights} onChange={insights => set({ insights })} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="copilot-budget" className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Reads per question (1–16)</label>
          <input id="copilot-budget" type="number" min={1} max={16} value={draft.toolCallBudget}
            onChange={e => set({ toolCallBudget: parseInt(e.target.value, 10) || 0 })}
            className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50" />
        </div>
        <div>
          <label htmlFor="copilot-memory" className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Earlier turns remembered (0–12)</label>
          <input id="copilot-memory" type="number" min={0} max={12} value={draft.memoryTurns}
            onChange={e => set({ memoryTurns: parseInt(e.target.value, 10) || 0 })}
            className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50" />
        </div>
      </div>

      {error && <p className="text-sm text-rose-300" role="alert">{error}</p>}

      <div className="flex items-center justify-between gap-4">
        <span className="text-xs text-slate-500">
          {view.updatedBy ? `Last changed by ${view.updatedBy}${view.updatedAt ? ` on ${new Date(view.updatedAt).toLocaleString()}` : ''}.` : 'Not changed from this screen yet.'}
          {!licensed && draft.enabled ? ' Saved settings take effect once the add-on is licensed.' : ''}
        </span>
        <button type="button" onClick={() => void save()} disabled={saving || !changed}
          className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 rounded-xl text-sm font-semibold text-white flex items-center gap-2">
          {saving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saved && !changed ? 'Saved' : 'Save Ask Axiom settings'}
        </button>
      </div>
    </div>
  );
};

export default AbfCopilotSettingsCard;
